/* eslint-disable react/button-has-type */
import React, { useState, useEffect, forwardRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './LotDetails.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTriangleExclamation, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Tooltip from '@mui/material/Tooltip';
import ItemCard from '../ItemCard/ItemCard';
import { LOT_STATUS } from '../../constants';
import { shippingApi } from '../../../../api/shipping';
import { updateReviewStatus } from '../../../../modules/shippingReview/thunks';

const LotDetails = forwardRef(({ lotInfo, openEditShippingModal, onConfirm }, ref) => {
  const dispatch = useDispatch();
  const shippingInfo = lotInfo?.shippingReviewData;
  const shipmentObjects = shippingInfo?.arguments?.objects || [];
  const objectCount = shipmentObjects.length || 0;
  const shippingLocation = shippingInfo?.arguments?.origin?.name || 'Unknown Location';
  const [showEditButton, setShowEditButton] = useState(true);
  const [lotStatus, setLotStatus] = useState();
  const artaSubtypeMap = useSelector((state) => state.shippingReview.artaSubtypeMap);
  // Polling logic starts
  const pollInterval = 30000; // 30000 = 30 seconds
  const stopPolling = () => {
    if (!ref.current) return;
    if (ref.current?.[lotInfo.lotID]) {
      clearInterval(ref.current[lotInfo.lotID]);
      ref.current[lotInfo.lotID] = null;
    }
  };
  const asyncPoll = async () => {
    try {
      const data = await shippingApi.fetchReviewById(lotInfo.shippingReviewData.id, lotInfo.houseID);
      if (!data || !data.data || !data.data.review || !lotInfo) {
        stopPolling();
        throw new Error('Error in polling review data, please refresh the page');
      }
      dispatch(updateReviewStatus(lotInfo.lotID, data.data?.review));
    } catch (err) {
      console.error('Error fetching review while polling:', err);
      stopPolling();
    }
  };
  const startPolling = () => {
    ref.current = { ...ref.current, [lotInfo.lotID]: setInterval(asyncPoll, pollInterval) };
  };
  useEffect(() => {
    if (!lotInfo?.shippingReviewData) return;
    const status = lotInfo.shippingReviewData.status;
    setLotStatus(status);
    if (status === 'completed') {
      setShowEditButton(false);
    }
  }, [lotInfo]);
  useEffect(() => {
    if (lotStatus === 'pending') {
      startPolling();
    } else {
      stopPolling();
    }
  }, [lotStatus]);
  // Polling logic ends

  const truncateTrailingZeros = (value) => parseFloat(value);

  const onClickEdit = () => {
    openEditShippingModal(lotInfo);
  };

  const onConfirmLot = () => {
    const payload = { review: { reviewComplete: true } };
    onConfirm(lotInfo.lotID, shippingInfo.id, lotInfo.houseID, payload);
  };

  return (
    <div className="lot-details" data-shipping-review-id={lotInfo.shippingReviewID}>
      <div className="lot-thumbnail">
        <img src={lotInfo.imageUrl} alt="Thumbnail" />
      </div>
      <div className="lot-info">
        <div className="lot-header">
          <strong>#{lotInfo.lotNumber}{lotInfo.lotExt} {lotInfo.lotTitle}</strong>
          {objectCount > 1 && <p>This lot has {objectCount} items shipping from {shippingLocation}</p>}
        </div>
        {objectCount > 1 && (
          shipmentObjects && shipmentObjects.map((item) => (
            <div className="lot-table" key={item.id}>
              <ItemCard
                dimensions={`${truncateTrailingZeros(item.height)} x ${truncateTrailingZeros(item.width)} x ${truncateTrailingZeros(item.depth)} ${item.unitOfMeasurement}`}
                type={artaSubtypeMap?.[item.artaSubtype]?.name}
                fragile={item.isFragile}
                name={item.title}
              />
            </div>
          ))
        )}
        {objectCount === 1 && (
          <div className="lot-table">
            <ItemCard
              dimensions={`${truncateTrailingZeros(shipmentObjects[0].height)}
                x ${truncateTrailingZeros(shipmentObjects[0].width)}
                x ${truncateTrailingZeros(shipmentObjects[0].depth)}
                ${shipmentObjects[0].unitOfMeasurement}`}
              type={artaSubtypeMap?.[shipmentObjects[0].artaSubtype]?.name}
              fulfillmentLocation={shippingLocation}
              fragile={shipmentObjects[0].isFragile}
              single
            />
          </div>
        )}
      </div>
      {/* TODO: move the rendering logic  into a renderActions utility function */}
      <div className="lot-actions">
        {showEditButton && <button className="detail-edit-button" onClick={onClickEdit}>Edit</button>}
        {lotStatus === 'awaiting_approval' && <button className="detail-confirm-button" onClick={onConfirmLot}>Confirm</button>}
        {lotStatus === 'completed' && <span className="success-text">{LOT_STATUS.completed} <FontAwesomeIcon icon={faCheckCircle} /></span>}
        {lotStatus === 'failed' && (
          <Tooltip title="There was an issue generating a shipping quote. Edit and Save to resolve" placement="left">
            <span id="needsReviewWarning" className="error-text">{LOT_STATUS.failed} <FontAwesomeIcon icon={faTriangleExclamation} /></span>
          </Tooltip>
        )}
        {(lotStatus === 'pending' || lotStatus === 'processing') && <span className="warning-text">{LOT_STATUS.pending} <FontAwesomeIcon icon={faSpinner} /></span>}
      </div>
    </div>
  );
});

export default LotDetails;
